import { ArrowUpRight } from "lucide-react";
import { useAdvertiseDialog } from "@/components/AdvertiseDialog";
import {
	SponsoredLeaderboardRow,
	SponsoredRecommendation,
	sponsorIndexForKey,
	useSponsors,
} from "@/components/SponsorPlacements";
import type { PublicAd } from "@/lib/agentmrr-ads";

/**
 * Pick what to show in a promo slot: rotate across the paid sponsor pool plus
 * a reserved "Advertise here" own-CTA slot (always shown, to everyone). `index`
 * is a stable slot ordinal so the same index always yields the same pick — no
 * per-render shuffle or hydration mismatch.
 */
function usePromoChoice(
	index: number,
): { kind: "ad"; ad: PublicAd } | { kind: "advertise" } {
	const sponsors = useSponsors();
	// Always reserve the own-CTA slot in the rotation base.
	const base = sponsors.length + 1;
	const pick = ((index % base) + base) % base;
	if (pick < sponsors.length) return { kind: "ad", ad: sponsors[pick] };
	return { kind: "advertise" };
}

/**
 * A promo leaderboard row. Rotates a paid sponsor row with the own-CTA. Mirrors
 * SponsoredLeaderboardRow styling so it reads as a promo break between rows.
 */
export function PromoLeaderboardRow({
	colSpan,
	ordinal = 0,
}: {
	colSpan: number;
	/** 0-based promo counter so consecutive promos round-robin the pool evenly. */
	ordinal?: number;
}) {
	const { openAdvertiseDialog } = useAdvertiseDialog();
	const choice = usePromoChoice(ordinal);
	if (choice.kind === "ad")
		return <SponsoredLeaderboardRow ad={choice.ad} colSpan={colSpan} />;

	return (
		<tr className="bg-brand-soft">
			<td colSpan={colSpan} className="border-t border-border/60 px-2 py-1.5">
				<button
					type="button"
					onClick={() => openAdvertiseDialog("leaderboard_promo")}
					className="group flex w-full items-center gap-3 text-left"
				>
					<div className="flex size-8 shrink-0 items-center justify-center border border-border bg-muted font-mono text-foreground">
						+
					</div>
					<div className="min-w-0 flex-1">
						<div className="flex min-w-0 items-center gap-2">
							<p
								className="truncate text-base text-foreground transition-colors group-hover:text-brand"
								style={{ fontWeight: 400 }}
							>
								Advertise your agent here — $99/mo
							</p>
							<span className="shrink-0 border border-border px-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
								Sponsored
							</span>
						</div>
						<p className="line-clamp-1 text-sm text-muted-foreground">
							Reach founders building with AI agents.
						</p>
					</div>
					<ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand" />
				</button>
			</td>
		</tr>
	);
}

/**
 * "Want more reach?" promo recommendation for agent profiles. Rotates a paid
 * sponsor card with the own-CTA.
 */
export function PromoRecommendation({ seed = "" }: { seed?: string }) {
	const { openAdvertiseDialog } = useAdvertiseDialog();
	const choice = usePromoChoice(sponsorIndexForKey(seed));
	if (choice.kind === "ad") return <SponsoredRecommendation ad={choice.ad} />;

	return (
		<section className="mt-12">
			<div className="flex items-baseline justify-between pb-3">
				<span className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Advertise here
				</span>
				<span className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
					$99/mo
				</span>
			</div>
			<button
				type="button"
				onClick={() => openAdvertiseDialog("profile_promo")}
				className="group flex w-full items-center gap-3 border border-border bg-background p-4 text-left transition-colors hover:border-brand/40 hover:bg-brand-soft"
			>
				<div className="flex size-10 shrink-0 items-center justify-center border border-border bg-muted font-mono text-foreground">
					+
				</div>
				<div className="min-w-0 flex-1">
					<p
						className="truncate text-base text-foreground transition-colors group-hover:text-brand"
						style={{ fontWeight: 400 }}
					>
						Put your agent in front of founders
					</p>
					<p className="line-clamp-1 text-sm text-muted-foreground">
						One month of placement — $99/mo
					</p>
				</div>
				<ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-brand" />
			</button>
		</section>
	);
}
